// static/src/stores/userStore.js
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export const useUserStore = defineStore('user', () => {
  // --- State ---
  const user = ref(null);
  const isAuthenticated = ref(false);
  const loading = ref(true);
  // Distinct from `loading` (which UI components use purely for a skeleton
  // state and defaults true on purpose): tracks whether fetchUser() has ever
  // completed, so the router guard below can tell "never fetched yet" apart
  // from "a fetch happens to be in flight." Conflating the two meant the
  // guard's `!userStore.loading` check was false on literally every first
  // page load (loading defaults true), so fetchUser() never ran and every
  // hard navigation to a requiresAuth route bounced to /login even with a
  // valid session cookie.
  const initialized = ref(false);
  const reportsRemaining = ref(0);
  // Added 2026-08-29 (owner: header should also show lead credits, not
  // just audits) -- same dedicated-ref pattern as reportsRemaining
  // itself, sourced from /api/auth/me's matching lead_credits_remaining/
  // _limit fields (src/api/auth.py, mirrors the audit-budget computation
  // right above it there).
  const leadCreditsRemaining = ref(0);
  const leadCreditsLimit = ref(0);
  const totalAnalyses = ref(0);
  const subscriptionTier = ref('starter');

  // --- Getters ---
  const isPremium = computed(() => ['growth', 'pro', 'professional', 'scale', 'business', 'enterprise', 'agency'].includes(subscriptionTier.value));
  const userName = computed(() => user.value?.first_name || user.value?.email?.split('@')[0] || 'User');
  const userFullName = computed(() => {
    const first = user.value?.first_name || '';
    const last = user.value?.last_name || '';
    return [first, last].filter(Boolean).join(' ');
  });
  const isAdmin = computed(() => user.value?.is_admin || false);

  // --- Helpers ---
  const clearUser = () => {
    user.value = null;
    isAuthenticated.value = false;
    reportsRemaining.value = 0;
    leadCreditsRemaining.value = 0;
    leadCreditsLimit.value = 0;
    totalAnalyses.value = 0;
    subscriptionTier.value = 'starter';
  };

  // --- Core API Methods ---

  // Prevent concurrent fetchUser() calls. The router beforeEach guard
  // and App.vue onMounted both call fetchUser() on initial page load
  // and can race (both see initialized=false before either completes).
  // Dedup lock ensures only one GET /api/auth/me is ever in-flight.
  let _fetchPromise = null;

  const fetchUser = async () => {
    if (_fetchPromise) return _fetchPromise;

    _fetchPromise = (async () => {
      loading.value = true;
      try {
        const response = await fetch('/api/auth/me', {
          credentials: 'include',
          headers: { 'Accept': 'application/json' }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            user.value = data.user;
            isAuthenticated.value = true;
            reportsRemaining.value = data.user.reports_remaining ?? 0;
            leadCreditsRemaining.value = data.user.lead_credits_remaining ?? 0;
            leadCreditsLimit.value = data.user.lead_credits_limit ?? 0;
            subscriptionTier.value = data.user.subscription_tier ?? 'starter';
          } else {
            clearUser();
          }
        } else {
          clearUser();
        }
      } catch (err) {
        console.error('Failed to fetch user:', err);
        clearUser();
      } finally {
        loading.value = false;
        initialized.value = true;
        _fetchPromise = null;
      }
    })();

    return _fetchPromise;
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await fetch('/api/dashboard-data', {
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          totalAnalyses.value = data.total_analyses || 0;
          if (data.reports_remaining !== undefined) {
            reportsRemaining.value = data.reports_remaining;
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
    }
  };

  // --- Authentication ---

  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember_me: rememberMe }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.user) {
        user.value = data.user;
        isAuthenticated.value = true;
        subscriptionTier.value = data.user.subscription_tier ?? 'starter';
        await fetchUser(); // SSOT: reports_remaining comes from /auth/me → AuditBudget → Redis
        return { success: true, redirect: data.redirect || '/dashboard' };
      }
      return { success: false, error: data.error || 'Login failed' };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch('/api/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success && data.user) {
        user.value = data.user;
        isAuthenticated.value = true;
        subscriptionTier.value = data.user.subscription_tier ?? 'starter';
        await fetchUser(); // SSOT: reports_remaining comes from /auth/me → AuditBudget → Redis
        return { success: true, redirect: data.redirect || '/dashboard' };
      }
      return { success: false, error: data.error || 'Registration failed' };
    } catch (err) {
      console.error('Registration error:', err);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // ===== FIXED LOGOUT =====
  const logout = async () => {
    let success = false;

    try {
      // Use window.fetchWithCSRF to guarantee CSRF token is attached
      // This is the same as the global fetch, but explicit to avoid any override issues
      const response = await window.fetchWithCSRF('/api/logout', {
        method: 'POST',
        credentials: 'include'
      });
      success = response.ok;
      if (!success) {
        console.warn('Logout request failed with status:', response.status);
      }
    } catch (err) {
      console.warn('Logout request error:', err);
    }

    // Always clear local state
    clearUser();

    // Delete all cookies manually (defensive)
    document.cookie.split(';').forEach(c => {
      const name = c.split('=')[0].trim();
      if (name) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
      }
    });

    // Redirect based on success
    if (success) {
      window.location.href = '/';
    } else {
      // Even if the server fails, go to login to force re‑authentication
      window.location.href = '/login';
    }
  };

  // --- Profile Management ---

  const updateProfile = async (profileData) => {
    try {
      const response = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        await fetchUser();
        return { success: true, message: 'Profile updated successfully' };
      }
      return { success: false, error: data.error || 'Update failed' };
    } catch (err) {
      console.error('Update profile error:', err);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        return { success: true, message: 'Password changed successfully' };
      }
      return { success: false, error: data.error || 'Password change failed' };
    } catch (err) {
      console.error('Change password error:', err);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const deleteAccount = async (password, confirmation) => {
    try {
      const response = await fetch('/api/user/delete-account', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, confirmation }),
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        clearUser();
        return { success: true };
      }
      return { success: false, error: data.error || 'Account deletion failed' };
    } catch (err) {
      console.error('Delete account error:', err);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  const fetchRevenueSettings = async () => {
    try {
      const response = await fetch('/api/settings/revenue', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Fetch revenue settings error:', err);
      return { success: false, error: 'Network error' };
    }
  };

  const updateRevenueSettings = async (settings) => {
    try {
      const response = await fetch('/api/settings/revenue', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
        credentials: 'include'
      });
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('Update revenue settings error:', err);
      return { success: false, error: 'Network error' };
    }
  };

  // --- Return API ---
  return {
    user,
    isAuthenticated,
    loading,
    initialized,
    reportsRemaining,
    leadCreditsRemaining,
    leadCreditsLimit,
    totalAnalyses,
    subscriptionTier,
    isPremium,
    userName,
    userFullName,
    isAdmin,
    fetchUser,
    fetchDashboardStats,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    deleteAccount,
    clearUser,
    fetchRevenueSettings,
    updateRevenueSettings
  };
});